import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CloseOutlined,
  MinusOutlined,
  PauseOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  SearchOutlined,
  TableOutlined,
} from '@ant-design/icons';
// Khung-bốn-góc và bàn tay mở không có trong `@ant-design/icons`, và cả hai là hình quy ước của thanh điều khiển khung
// nhìn. `lucide-react` là thư viện icon điều hướng sẵn có của kho — ngoại lệ hẹp đúng hai hình.
import { Hand, Scan } from 'lucide-react';

import { Badge, Button, Input } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { soVN } from '../../../../lib/dinh-dang';

/**
 * Lược đồ một nguồn, vẽ thành hình (port `SoDoLuocDo.tsx` native): bảng là một khối CÓ CỘT, quan hệ đã khai là đường
 * nối giữa hai dòng cột cụ thể — câu "nối bằng cột gì" trả lời được bằng mắt, không bằng rê chuột đọc tooltip.
 *
 * - Bố cục theo CỘT DỌC (khối cao theo số cột); phân tầng BFS từ bảng nhiều quan hệ nhất của mỗi cụm.
 * - Lực lượng `1` / `0..*` thay mũi tên cho `fk`; `shared_dimension` nét đứt và KHÔNG nhãn lực lượng — khai lực lượng
 *   cho quan hệ đối xứng là bịa ra một ràng buộc.
 * - "Chưa khai quan hệ nào" được nói to: bản sao không có khoá ngoại, quan hệ trong bản khai là thứ DUY NHẤT cho mô
 *   hình biết được phép `JOIN` gì.
 * - Chế độ chạy vẽ **đường đi của một câu JOIN**, không phải luồng đồng bộ — nói rõ trên màn hình.
 *
 * Màu trong SVG đi bằng biến CSS của token (`var(--…)`) hoặc class thang màu — tự đổi theo chủ đề.
 */

export interface CotLuocDo {
  name: string;
  type?: string;
  label?: string | null;
  nullable?: boolean;
  unit?: string | null;
  additive?: boolean | null;
  enumValues?: { value: string; meaning?: string }[];
  nullMeaning?: string | null;
}

export interface BangLuocDo {
  name: string;
  title?: string | null;
  grain?: string | null;
  primaryKey?: string[];
  columns?: CotLuocDo[];
  links?: { column: string; toTable: string; toColumn: string; kind?: string }[];
  enabled?: boolean;
}

const W = 264;
const DAU = 30;
const DONG = 21;
const CHAN = 22;
const KHE_X = 168;
/** Giữa hai cột LẺ LOI không có cạnh nào luồn qua, nên khe hẹp hơn. */
const KHE_LE = 52;
const KHE_Y = 40;

/** Cắt theo RANH GIỚI TỪ — cắt giữa từ đọc như dữ liệu hỏng. */
function catTheoTu(s: string, max: number) {
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const i = cut.lastIndexOf(' ');
  return `${(i > max * 0.6 ? cut.slice(0, i) : cut).replace(/[\s—·-]+$/, '')}…`;
}

/**
 * Giới hạn phóng theo TỶ LỆ THẬT trên màn hình, không theo `k`: `viewBox` tự co lược đồ cho vừa khung nên hệ số quy đổi
 * phụ thuộc lược đồ lớn cỡ nào — kẹp `k` thì trần mang nghĩa khác nhau ở mỗi nguồn.
 */
const MIN_TY_LE = 0.02;
const MAX_TY_LE = 4;

/** `tro` = bấm để chọn bảng; `tay` = chỉ kéo hình. */
type CongCu = 'tro' | 'tay';

interface ONut {
  t: BangLuocDo;
  x: number;
  y: number;
  cao: number;
  cot: string[];
}

const MAU = {
  muted: 'var(--text-muted)',
  primary: 'var(--text-primary)',
  secondary: 'var(--text-secondary)',
  surface: 'var(--bg-surface)',
  subtle: 'var(--bg-subtle)',
  strong: 'var(--border-strong)',
  default: 'var(--border-default)',
};

export function SoDoLuocDo({ tables }: { tables: BangLuocDo[] }) {
  const { t, locale } = useT();
  const [chon, setChon] = useState<string | null>(null);
  const [chay, setChay] = useState(false);
  const [loc, setLoc] = useState('');
  const [view, setView] = useState({ k: 1, x: 0, y: 0 });
  const [congCu, setCongCu] = useState<CongCu>('tro');
  const svgRef = useRef<SVGSVGElement>(null);
  const keo = useRef<{ x: number; y: number; vx: number; vy: number } | null>(null);
  /** Vừa KÉO hay chỉ BẤM — không có cờ này thì mọi lần kéo qua một khối đều chọn oan bảng dưới con trỏ. */
  const daKeo = useRef(false);

  const bang = useMemo(() => tables.filter((x) => x.enabled !== false), [tables]);

  const { nut, canh, w, h } = useMemo(() => {
    const ten = bang.map((x) => x.name);
    const co = new Set(ten);
    const ke = new Map<string, Set<string>>(ten.map((n) => [n, new Set<string>()]));
    for (const b of bang)
      for (const l of b.links ?? [])
        if (co.has(l.toTable) && l.toTable !== b.name) {
          ke.get(b.name)?.add(l.toTable);
          ke.get(l.toTable)?.add(b.name);
        }
    const keCua = (n: string) => ke.get(n) ?? new Set<string>();
    const theoTen = new Map(bang.map((b) => [b.name, b]));
    const cotCua = new Map(bang.map((b) => [b.name, (b.columns ?? []).map((c) => c.name)]));
    const caoCua = (n: string) => {
      const b = theoTen.get(n);
      return DAU + (b?.columns?.length ?? 0) * DONG + (b?.primaryKey?.length ? CHAN : 0) + 8;
    };

    // Phân tầng: BFS từ bảng nhiều quan hệ nhất của mỗi cụm — gần như luôn là bảng sự kiện trung tâm.
    const bacCua = new Map<string, number>();
    const daXet = new Set<string>();
    const goc = [...ten].sort((a, b) => keCua(b).size - keCua(a).size || a.localeCompare(b));
    for (const g of goc) {
      if (daXet.has(g)) continue;
      daXet.add(g);
      bacCua.set(g, 0);
      const hang: string[] = [g];
      while (hang.length) {
        const n = hang.shift() as string;
        for (const m of [...keCua(n)].sort()) {
          if (daXet.has(m)) continue;
          daXet.add(m);
          bacCua.set(m, (bacCua.get(n) ?? 0) + 1);
          hang.push(m);
        }
      }
    }

    // Bảng không nối với bảng nào: dồn về các cột cuối — vẫn phải hiện (mọi câu cần JOIN sẽ hỏng ở đó).
    const leLoi = ten.filter((n) => keCua(n).size === 0).sort();
    const noiNhau = ten.filter((n) => keCua(n).size > 0);
    const bacToiDa = Math.max(0, ...noiNhau.map((n) => bacCua.get(n) ?? 0));

    const theoCot = new Map<number, string[]>();
    for (const n of noiNhau) {
      const b = bacCua.get(n) ?? 0;
      theoCot.set(b, [...(theoCot.get(b) ?? []), n]);
    }
    // Gói bảng lẻ loi theo tỷ lệ gần khung 16:9 (n ≈ √(1,78 · tổng chiều cao / bước cột)) — gói theo ngưỡng cố định thì
    // lược đồ chưa khai quan hệ nào thành một dải rộng 17.000 điểm ảnh.
    const caoLe = leLoi.map((n) => caoCua(n) + KHE_Y);
    const tongCaoLe = caoLe.reduce((a, c) => a + c, 0);
    const soCotLe = Math.max(1, Math.round(Math.sqrt((1.78 * tongCaoLe) / (W + KHE_LE))));
    const nguong = Math.max(
      ...caoLe,
      Math.max(
        tongCaoLe / soCotLe,
        ...[...theoCot.values()].map((ds) => ds.reduce((a, n) => a + caoCua(n) + KHE_Y, 0)),
      ),
    );
    const cotLeDau = bacToiDa + (noiNhau.length ? 1 : 0);
    let cotLe = cotLeDau;
    let caoDangXep = 0;
    leLoi.forEach((n, i) => {
      const c = caoLe[i] ?? 0;
      if (caoDangXep > 0 && caoDangXep + c > nguong) {
        cotLe += 1;
        caoDangXep = 0;
      }
      theoCot.set(cotLe, [...(theoCot.get(cotLe) ?? []), n]);
      caoDangXep += c;
    });

    const bacs = [...theoCot.keys()].sort((a, b) => a - b);
    const xCua = new Map<number, number>();
    let xChay = 0;
    for (const b of bacs) {
      xCua.set(b, xChay);
      xChay += W + (b >= cotLeDau ? KHE_LE : KHE_X);
    }
    const viTri = new Map<string, { x: number; y: number; cao: number }>();

    // Sắp trong cột theo TRỌNG TÂM của bảng đã đặt ở cột trước — xếp theo tên thì cạnh cắt ngang mọi thứ. Hai lượt.
    const xepMotLuot = () => {
      for (const b of bacs) {
        const ds = theoCot.get(b) ?? [];
        if (b > 0)
          ds.sort((m, n) => {
            const tt = (x: string) => {
              const y = [...keCua(x)]
                .map((z) => viTri.get(z))
                .filter((z): z is { x: number; y: number; cao: number } => Boolean(z))
                .map((z) => z.y + z.cao / 2);
              return y.length ? y.reduce((a, c) => a + c, 0) / y.length : Number.MAX_SAFE_INTEGER;
            };
            return tt(m) - tt(n) || m.localeCompare(n);
          });
        let y = 0;
        for (const n of ds) {
          const cao = caoCua(n);
          viTri.set(n, { x: xCua.get(b) ?? 0, y, cao });
          y += cao + KHE_Y;
        }
      }
    };
    xepMotLuot();
    xepMotLuot();

    const dayCot = (b: number) => {
      const cuoi = (theoCot.get(b) ?? []).at(-1);
      const v = cuoi ? viTri.get(cuoi) : undefined;
      return v ? v.y + v.cao : 0;
    };
    const caoNhat = Math.max(1, ...bacs.map(dayCot));
    for (const b of bacs) {
      const lech = (caoNhat - dayCot(b)) / 2;
      for (const n of theoCot.get(b) ?? []) {
        const p = viTri.get(n);
        if (p) p.y += lech;
      }
    }

    const LE = 48;
    const nut: ONut[] = bang.flatMap((b) => {
      const v = viTri.get(b.name);
      return v ? [{ t: b, x: v.x + LE, y: v.y + LE, cao: v.cao, cot: cotCua.get(b.name) ?? [] }] : [];
    });
    const vt = new Map(nut.map((n) => [n.t.name, n]));
    const w = (xCua.get(bacs.at(-1) ?? 0) ?? 0) + W + LE * 2;
    const h = caoNhat + LE * 2;

    /** Tâm dòng của một cột; cột chưa khai thì neo vào đầu khối — neo sai chỗ còn hơn nuốt mất một quan hệ. */
    const yCua = (n: ONut, cot: string) => {
      const i = n.cot.indexOf(cot);
      return i < 0 ? n.y + DAU / 2 : n.y + DAU + i * DONG + DONG / 2;
    };

    const canh = bang
      .flatMap((b) => (b.links ?? []).map((l) => ({ b, l })))
      .flatMap(({ b, l }, idx) => {
        const a = vt.get(b.name);
        const z = vt.get(l.toTable);
        if (!a || !z || l.toTable === b.name) return [];
        const sy = yCua(a, l.column);
        const ey = yCua(z, l.toColumn);
        const sangPhai = z.x > a.x;
        const cungCot = z.x === a.x;
        // Cùng cột thì CẢ HAI đầu ra mép trái — neo một đầu mép phải rồi vòng qua trái sẽ xuyên ngang khối.
        const sx = sangPhai ? a.x + W : a.x;
        const ex = cungCot ? z.x : sangPhai ? z.x : z.x + W;
        // Chỗ rẽ lệch nhau vài điểm ảnh theo chỉ số: hai cạnh song song rẽ cùng một hoành độ đè lên nhau thành một nét.
        const mx = cungCot ? a.x - 36 - (idx % 4) * 12 : (sx + ex) / 2 + ((idx % 5) - 2) * 9;
        return [
          {
            id: `${b.name}.${l.column}->${l.toTable}.${l.toColumn}-${idx}`,
            tu: b.name,
            den: l.toTable,
            cot: l.column,
            denCot: l.toColumn,
            chung: l.kind !== 'fk',
            d: `M ${sx} ${sy} L ${mx} ${sy} L ${mx} ${ey} L ${ex} ${ey}`,
            sx,
            sy,
            ex,
            ey,
            slx: sx + (sx === a.x ? -13 : 13),
            elx: ex + (ex === z.x ? -13 : 13),
          },
        ];
      });

    return { nut, canh, w, h };
  }, [bang]);

  const dangChon = bang.find((x) => x.name === chon) ?? null;
  const noiVoi = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const c of canh) {
      m.set(c.tu, (m.get(c.tu) ?? new Set<string>()).add(c.den));
      m.set(c.den, (m.get(c.den) ?? new Set<string>()).add(c.tu));
    }
    return m;
  }, [canh]);
  const lienQuan = chon ? (noiVoi.get(chon) ?? new Set<string>()) : new Set<string>();

  /** Dòng cột đang tham gia quan hệ của bảng đang chọn — tô nền để mắt nhảy thẳng tới cột nối. */
  const cotSang = useMemo(() => {
    const s = new Set<string>();
    if (!chon) return s;
    for (const c of canh)
      if (c.tu === chon || c.den === chon) {
        s.add(`${c.tu}.${c.cot}`);
        s.add(`${c.den}.${c.denCot}`);
      }
    return s;
  }, [canh, chon]);

  const bangLoc = useMemo(() => {
    const q = loc.trim().toLowerCase();
    if (!q) return bang;
    return bang.filter((x) => `${x.name} ${x.title ?? ''}`.toLowerCase().includes(q));
  }, [bang, loc]);

  /** Toạ độ màn hình → lược đồ, tính cả lề do `preserveAspectRatio` căn giữa — bỏ qua thì phóng theo con trỏ lệch dần. */
  const toaDo = (cx: number, cy: number) => {
    const r = svgRef.current?.getBoundingClientRect();
    if (!r) return { x: 0, y: 0 };
    const s = Math.min(r.width / w, r.height / h);
    return { x: (cx - r.left - (r.width - w * s) / 2) / s, y: (cy - r.top - (r.height - h * s) / 2) / s };
  };

  const tyLeKhung = () => {
    const r = svgRef.current?.getBoundingClientRect();
    return r && r.width && r.height ? Math.min(r.width / w, r.height / h) : 1;
  };

  /** Phóng quanh một điểm: điểm ấy đứng yên dưới con trỏ. */
  const phong = (heSo: number, cx?: number, cy?: number) =>
    setView((v) => {
      const co = tyLeKhung();
      const k = Math.min(MAX_TY_LE / co, Math.max(MIN_TY_LE / co, v.k * heSo));
      if (cx === undefined || cy === undefined) return { ...v, k };
      const u = toaDo(cx, cy);
      return { k, x: u.x - ((u.x - v.x) / v.k) * k, y: u.y - ((u.y - v.y) / v.k) * k };
    });

  const veGiua = (ten: string) => {
    const n = nut.find((k) => k.t.name === ten);
    if (!n) return;
    setView((v) => ({ ...v, x: w / 2 - (n.x + W / 2) * v.k, y: h / 2 - (n.y + n.cao / 2) * v.k }));
  };

  const vuaKhung = () => setView({ k: 1, x: 0, y: 0 });

  // `view.k = 1` KHÔNG phải tỷ lệ 1:1 (viewBox tự co) — đo khung để hiện đúng phần trăm người dùng đang nhìn.
  const [khung, setKhung] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = svgRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((mucs) => {
      const r = mucs[0]?.contentRect;
      if (r) setKhung({ w: r.width, h: r.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const tyLeVua = khung.w && khung.h ? Math.min(khung.w / w, khung.h / h) : 1;

  // Phím tắt như dbdiagram, gắn ở `window`: khung chỉ nhận phím khi giữ focus, bấm ở cột bên phải xong là phím tắt chết.
  useEffect(() => {
    const nghe = (e: KeyboardEvent) => {
      const o = e.target as HTMLElement | null;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (o && (o.tagName === 'INPUT' || o.tagName === 'TEXTAREA' || o.isContentEditable)) return;
      if (e.key === '1') setView({ k: 1, x: 0, y: 0 });
      else if (e.key.toLowerCase() === 'h') setCongCu('tay');
      else if (e.key.toLowerCase() === 'v') setCongCu('tro');
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', nghe);
    return () => window.removeEventListener('keydown', nghe);
  }, []);

  if (!bang.length) {
    return <p className="py-10 text-center text-sm text-content-muted">{t('troLyAiKetNoiSoDo.rongMoTa')}</p>;
  }

  const canhChay = (c: { tu: string; den: string }) => chay && (!chon || c.tu === chon || c.den === chon);
  const so = (n: number) => soVN(n, locale);

  return (
    <div className="grid h-full min-h-0 gap-4 lg:grid-cols-[1fr_320px]">
      <style>{`
        @keyframes so-do-chay { to { stroke-dashoffset: -28; } }
        .so-do-chay { stroke-dasharray: 6 8; animation: so-do-chay .7s linear infinite; }
        @media (prefers-reduced-motion: reduce) {
          /* Không tắt hẳn: đứng im thì "đang chạy" và "đã dừng" trông y hệt — bỏ nét đứt để vẫn phân biệt bằng hình. */
          .so-do-chay { animation: none; stroke-dasharray: none; }
        }
      `}</style>

      {/* Lưới chấm vẽ bằng CSS của KHUNG, không bằng `<pattern>` trong SVG — `viewBox` co chấm tới mức biến mất. */}
      <div
        className="relative min-h-0 overflow-hidden rounded-lg border border-default bg-page"
        style={{
          backgroundImage: 'radial-gradient(circle, var(--border-default) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
        }}
      >
        <div className="absolute start-3 top-3 z-10 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant={chay ? 'primary' : 'secondary'}
            icon={chay ? <PauseOutlined /> : <PlayCircleOutlined />}
            onClick={() => setChay((v) => !v)}
            disabled={canh.length === 0}
          >
            {chay ? t('troLyAiKetNoiSoDo.dung') : t('troLyAiKetNoiSoDo.chayDongChay')}
          </Button>
          {chay ? (
            <Badge variant="primary">
              {chon ? t('troLyAiKetNoiSoDo.duongJoinCua', { ten: chon }) : t('troLyAiKetNoiSoDo.moiDuongJoin')}
            </Badge>
          ) : null}
        </div>

        {/* Cụm nút khung hình — góc dưới trái, luôn hiện. Hai nhóm: mức phóng (con số) và bàn tay (một CHẾ ĐỘ). */}
        <div className="absolute bottom-4 start-4 z-10 flex items-center gap-0.5 rounded-lg border border-default bg-surface px-1.5 py-1 shadow-lg">
          <Button
            size="sm"
            variant="ghost"
            aria-label={t('troLyAiKetNoiSoDo.thuNho')}
            title={t('troLyAiKetNoiSoDo.thuNho')}
            icon={<MinusOutlined />}
            onClick={() => phong(1 / 1.25)}
          />
          <span className="w-12 text-center text-xs font-medium tabular-nums text-content-secondary">
            {so(Math.round(view.k * tyLeVua * 100))}%
          </span>
          <Button
            size="sm"
            variant="ghost"
            aria-label={t('troLyAiKetNoiSoDo.phongTo')}
            title={t('troLyAiKetNoiSoDo.phongTo')}
            icon={<PlusOutlined />}
            onClick={() => phong(1.25)}
          />
          <span className="mx-1 h-5 w-px border-s border-default" aria-hidden />
          {/* Phím tắt in ngay trên nút: gợi ý chỉ hiện khi rê chuột là gợi ý không ai học được. */}
          <Button
            size="sm"
            variant="ghost"
            aria-label={t('troLyAiKetNoiSoDo.vuaKhung')}
            title={`${t('troLyAiKetNoiSoDo.vuaKhung')} · 1`}
            onClick={vuaKhung}
            icon={
              <span className="inline-flex items-end gap-px">
                <Scan size={14} strokeWidth={1.75} aria-hidden />
                <span className="text-2xs leading-none text-content-muted">1</span>
              </span>
            }
          />
          <Button
            size="sm"
            variant={congCu === 'tay' ? 'primary' : 'ghost'}
            aria-label={t('troLyAiKetNoiSoDo.congCuTay')}
            aria-pressed={congCu === 'tay'}
            title={`${t('troLyAiKetNoiSoDo.congCuTay')} · H`}
            onClick={() => setCongCu((c) => (c === 'tay' ? 'tro' : 'tay'))}
            icon={
              <span className="inline-flex items-end gap-px">
                <Hand size={14} strokeWidth={1.75} aria-hidden />
                <span
                  className={cn(
                    'text-2xs leading-none',
                    congCu === 'tay' ? 'text-content-inverse' : 'text-content-muted',
                  )}
                >
                  H
                </span>
              </span>
            }
          />
        </div>

        <svg
          ref={svgRef}
          viewBox={`0 0 ${w} ${h}`}
          className={cn(
            'h-full min-h-[420px] w-full overscroll-contain',
            congCu === 'tay' ? 'cursor-grab active:cursor-grabbing' : 'cursor-default',
          )}
          role="img"
          aria-label={t('troLyAiKetNoiSoDo.altSoDo', { bang: so(bang.length), canh: so(canh.length) })}
          onWheel={(e) => phong(e.deltaY < 0 ? 1.12 : 1 / 1.12, e.clientX, e.clientY)}
          onMouseDown={(e) => {
            daKeo.current = false;
            keo.current = { x: e.clientX, y: e.clientY, vx: view.x, vy: view.y };
          }}
          onMouseMove={(e) => {
            const k = keo.current;
            const r = svgRef.current?.getBoundingClientRect();
            if (!k || !r) return;
            const sc = Math.min(r.width / w, r.height / h) || 1;
            // Ngưỡng 3px: chuột run vài điểm ảnh lúc bấm là chuyện thường.
            if (Math.abs(e.clientX - k.x) + Math.abs(e.clientY - k.y) > 3) daKeo.current = true;
            setView((v) => ({ ...v, x: k.vx + (e.clientX - k.x) / sc, y: k.vy + (e.clientY - k.y) / sc }));
          }}
          onMouseUp={() => (keo.current = null)}
          onMouseLeave={() => (keo.current = null)}
        >
          <g transform={`translate(${view.x} ${view.y}) scale(${view.k})`}>
            {canh.map((c) => {
              const mo = chon && c.tu !== chon && c.den !== chon;
              const dangChay = canhChay(c);
              const noi = chon === c.tu || chon === c.den;
              const mau = c.chung ? 'var(--color-violet-500)' : 'var(--color-primary-500)';
              return (
                <g key={c.id} opacity={mo ? 0.12 : 1}>
                  <title>{`${c.tu}.${c.cot} ↔ ${c.den}.${c.denCot}`}</title>
                  <path
                    d={c.d}
                    fill="none"
                    stroke={mau}
                    className={dangChay ? 'so-do-chay' : undefined}
                    strokeWidth={dangChay ? 2.25 : noi ? 2 : 1.25}
                    strokeDasharray={dangChay ? undefined : c.chung ? '5 5' : undefined}
                    strokeLinejoin="round"
                  />
                  <circle cx={c.sx} cy={c.sy} r={2.5} fill={mau} />
                  <circle cx={c.ex} cy={c.ey} r={2.5} fill={mau} />
                  {/* Lực lượng chỉ vẽ cho `fk`. */}
                  {c.chung ? null : (
                    <>
                      <text x={c.slx} y={c.sy - 4} textAnchor="middle" fontSize={9} fontWeight={500} fill={MAU.muted}>
                        0..*
                      </text>
                      <text x={c.elx} y={c.ey - 4} textAnchor="middle" fontSize={9} fontWeight={500} fill={MAU.muted}>
                        1
                      </text>
                    </>
                  )}
                  {dangChay ? (
                    <circle r={4} fill={mau}>
                      <animateMotion dur="1.9s" repeatCount="indefinite" path={c.d} />
                    </circle>
                  ) : null}
                </g>
              );
            })}

            {nut.map((n) => {
              const b = n.t;
              const la = b.name === chon;
              const canhBen = lienQuan.has(b.name);
              const mo = chon && !la && !canhBen;
              const soNoi = noiVoi.get(b.name)?.size ?? 0;
              const pk = new Set(b.primaryKey ?? []);
              const fk = new Set((b.links ?? []).map((l) => l.column));
              return (
                <g
                  key={b.name}
                  transform={`translate(${n.x} ${n.y})`}
                  opacity={mo ? 0.25 : 1}
                  className={congCu === 'tay' ? 'cursor-grab' : 'cursor-pointer'}
                  onClick={() => {
                    if (congCu === 'tay' || daKeo.current) return;
                    setChon(la ? null : b.name);
                  }}
                >
                  <rect
                    width={W}
                    height={n.cao}
                    rx={8}
                    fill={MAU.surface}
                    stroke={la ? 'var(--color-primary-500)' : MAU.strong}
                    strokeWidth={la ? 2 : 1}
                  />
                  {/* Đầu khối tối ở MỌI chủ đề: mốc để mắt tách khối này khỏi khối kia lúc thu nhỏ. */}
                  <path
                    d={`M 0 8 A 8 8 0 0 1 8 0 L ${W - 8} 0 A 8 8 0 0 1 ${W} 8 L ${W} ${DAU} L 0 ${DAU} Z`}
                    fill={la ? 'var(--color-primary-700)' : 'var(--color-dark-800)'}
                  />
                  <text x={10} y={20} fontSize={12} fontWeight={600} fill="var(--color-neutral-0)">
                    {catTheoTu(b.name, 26)}
                  </text>
                  {soNoi === 0 ? (
                    <text
                      x={W - 10}
                      y={20}
                      textAnchor="end"
                      fontSize={9}
                      fontWeight={500}
                      fill="var(--color-warning-500)"
                    >
                      {t('troLyAiKetNoiSoDo.chuaNoi')}
                    </text>
                  ) : null}

                  {(b.columns ?? []).map((c, i) => {
                    const y = DAU + i * DONG;
                    const sang = cotSang.has(`${b.name}.${c.name}`);
                    const coMa = Boolean(c.enumValues?.length);
                    const nn = c.nullable === false;
                    return (
                      <g key={c.name}>
                        <title>{`${c.name}${c.label ? ` — ${c.label}` : ''}`}</title>
                        {sang ? (
                          <rect x={1} y={y} width={W - 2} height={DONG} fill="var(--color-primary-50)" />
                        ) : i % 2 === 1 ? (
                          <rect x={1} y={y} width={W - 2} height={DONG} fill={MAU.subtle} />
                        ) : null}
                        {/* Máng trái cố định cho dấu khoá/nối: quét mắt một lượt là thấy hết khoá. */}
                        {pk.has(c.name) ? (
                          <text x={8} y={y + 14} fontSize={8} fontWeight={700} fill="var(--color-primary-700)">
                            PK
                          </text>
                        ) : fk.has(c.name) ? (
                          <text x={8} y={y + 14} fontSize={8} fontWeight={700} fill="var(--color-violet-700)">
                            FK
                          </text>
                        ) : null}
                        <text
                          x={28}
                          y={y + 14}
                          fontSize={10.5}
                          fontFamily="var(--font-mono, ui-monospace, monospace)"
                          fontWeight={pk.has(c.name) ? 600 : 400}
                          fill={pk.has(c.name) ? MAU.primary : MAU.secondary}
                        >
                          {catTheoTu(c.name, 22)}
                        </text>
                        {nn ? (
                          <text x={W - 10} y={y + 14} textAnchor="end" fontSize={8} fontWeight={700} fill={MAU.muted}>
                            NN
                          </text>
                        ) : null}
                        {coMa ? (
                          <text
                            x={W - (nn ? 30 : 10)}
                            y={y + 14}
                            textAnchor="end"
                            fontSize={8}
                            fontWeight={700}
                            fill="var(--color-teal-700)"
                          >
                            E
                          </text>
                        ) : null}
                        <text
                          x={W - (nn ? 30 : 10) - (coMa ? 12 : 0)}
                          y={y + 14}
                          textAnchor="end"
                          fontSize={9}
                          fontFamily="var(--font-mono, ui-monospace, monospace)"
                          fill={MAU.muted}
                        >
                          {catTheoTu(c.type ?? '', 12)}
                        </text>
                      </g>
                    );
                  })}

                  {b.primaryKey?.length ? (
                    <>
                      <line x1={1} y1={n.cao - CHAN} x2={W - 1} y2={n.cao - CHAN} stroke={MAU.default} />
                      <text
                        x={10}
                        y={n.cao - 7}
                        fontSize={9}
                        fontFamily="var(--font-mono, ui-monospace, monospace)"
                        fill={MAU.muted}
                      >
                        {catTheoTu(`(${b.primaryKey.join(', ')})`, 34)}
                      </text>
                    </>
                  ) : null}
                </g>
              );
            })}
          </g>
        </svg>
      </div>

      {/* `min-h-0` KHÔNG được bỏ: thiếu nó thì danh sách 41 bảng kéo hàng lưới cao vượt hộp thoại và cụm nút ở đáy canvas
          rơi khỏi tầm nhìn. */}
      <div className="flex min-h-0 min-w-0 flex-col gap-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-content-muted">
          <span>{t('troLyAiKetNoiSoDo.demBangQuanHe', { bang: so(bang.length), canh: so(canh.length) })}</span>
          <span className="inline-flex items-center gap-1.5">
            <svg width="20" height="8" aria-hidden>
              <line x1="0" y1="4" x2="20" y2="4" strokeWidth="1.5" stroke="var(--color-primary-500)" />
            </svg>
            {t('troLyAiKetNoiSoDo.chuThichFk')}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <svg width="20" height="8" aria-hidden>
              <line
                x1="0"
                y1="4"
                x2="20"
                y2="4"
                strokeWidth="1.5"
                strokeDasharray="5 5"
                stroke="var(--color-violet-500)"
              />
            </svg>
            {t('troLyAiKetNoiSoDo.chuThichChieu')}
          </span>
          <span>{t('troLyAiKetNoiSoDo.chuThichNhan')}</span>
        </div>

        {chay ? (
          <p className="rounded-lg bg-primary-50 px-3 py-2 text-xs leading-relaxed text-primary-700">
            {t('troLyAiKetNoiSoDo.ghiChayDongChay')}
          </p>
        ) : null}

        {canh.length === 0 ? (
          // Không phải trạng thái rỗng — là một phát hiện.
          <div className="rounded-lg bg-warning-50 p-3 text-xs leading-relaxed text-warning-700">
            <p className="font-semibold">{t('troLyAiKetNoiSoDo.chuaKhaiQuanHeTieuDe')}</p>
            <p className="mt-1">{t('troLyAiKetNoiSoDo.chuaKhaiQuanHeMo')}</p>
          </div>
        ) : null}

        {dangChon ? (
          // Khối trên khung hình đã nói tên cột, kiểu, khoá, NN — ở đây CHỈ phần lời khai nghiệp vụ.
          <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-default bg-surface p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate font-medium text-content-primary">{dangChon.title || dangChon.name}</p>
                <p className="truncate font-mono text-xs text-content-muted">{dangChon.name}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setChon(null)}>
                {t('troLyAiKetNoiSoDo.boChon')}
              </Button>
            </div>
            {dangChon.grain ? (
              <p className="mt-1.5 text-xs italic leading-relaxed text-content-muted">
                {t('troLyAiKetNoiSoDo.motDongLa', { grain: dangChon.grain })}
              </p>
            ) : null}

            {lienQuan.size > 0 ? (
              <div className="mt-2.5 flex flex-wrap gap-1">
                {[...lienQuan].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className="rounded-full"
                    onClick={() => {
                      setChon(n);
                      veGiua(n);
                    }}
                  >
                    <Badge className="cursor-pointer">{n}</Badge>
                  </button>
                ))}
              </div>
            ) : null}

            <ul className="mt-2.5 space-y-1">
              {(dangChon.columns ?? [])
                .filter((c) => c.label || c.unit || c.enumValues?.length || c.nullMeaning)
                .map((c) => (
                  <li key={c.name} className="flex items-baseline gap-1.5 text-xs">
                    <span className="font-mono text-content-secondary">{c.name}</span>
                    {c.label ? <span className="truncate text-content-muted">{c.label}</span> : null}
                    {c.unit ? <span className="text-content-muted">· {c.unit}</span> : null}
                    {c.enumValues?.length ? (
                      <span className="text-teal-700">· {t('troLyAiKetNoiSoDo.soMa', { n: so(c.enumValues.length) })}</span>
                    ) : null}
                  </li>
                ))}
            </ul>
          </div>
        ) : (
          // Danh sách bảng thay cho một dòng gợi ý; lọc theo cả tên kỹ thuật lẫn tên hiển thị.
          <>
            <Input
              size="small"
              aria-label={t('troLyAiKetNoiSoDo.locBang')}
              placeholder={t('troLyAiKetNoiSoDo.locBang')}
              value={loc}
              onChange={(e) => setLoc(e.target.value)}
              prefix={<SearchOutlined className="text-content-muted" />}
              suffix={
                loc ? (
                  <button type="button" aria-label={t('chung.xoaBoLoc')} onClick={() => setLoc('')}>
                    <CloseOutlined className="text-content-muted" />
                  </button>
                ) : null
              }
            />
            <ul aria-label={t('troLyAiKetNoiSoDo.danhSachBang')} className="min-h-0 flex-1 space-y-1 overflow-auto">
              {bangLoc.length === 0 ? (
                <li className="px-2.5 py-3 text-xs text-content-muted">{t('troLyAiKetNoiSoDo.khongKhop', { loc })}</li>
              ) : null}
              {bangLoc.map((b) => {
                const soNoi = noiVoi.get(b.name)?.size ?? 0;
                return (
                  <li key={b.name}>
                    <button
                      type="button"
                      onClick={() => {
                        setChon(b.name);
                        veGiua(b.name);
                      }}
                      className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-start transition-colors hover:bg-subtle"
                    >
                      <TableOutlined
                        aria-hidden
                        className={cn('shrink-0', soNoi ? 'text-primary-600' : 'text-warning-600')}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-xs text-content-secondary">{b.title || b.name}</span>
                        <span className="block truncate font-mono text-xs text-content-muted">
                          {b.name} · {t('troLyAiKetNoiSoDo.soCot', { n: so(b.columns?.length ?? 0) })}
                        </span>
                      </span>
                      <span className="shrink-0 text-xs text-content-muted">
                        {soNoi ? t('troLyAiKetNoiSoDo.soNoi', { n: so(soNoi) }) : t('troLyAiKetNoiSoDo.chuaNoi')}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
